import { NestedCheckboxItem } from './src/nested-checkbox-list.type';

/**
 * Checks if the item is partially selected based on the isSelected function.
 * used to set checkboxes to indeterminated state
 */
export const isPartiallySelected = (item: NestedCheckboxItem, isSelected: (id: string) => boolean) => {
  if (!item.children) return false;
  let partial = false;
  let anySelected = false;

  item.children.forEach((child: NestedCheckboxItem) => {
    const IS_FULLY_OR_PARTIALLY_SELECTED = isSelected(child.id) || isPartiallySelected(child, isSelected);
    if (IS_FULLY_OR_PARTIALLY_SELECTED) {
      anySelected = true;
    }
    if (!IS_FULLY_OR_PARTIALLY_SELECTED) {
      partial = true;
    }
  });

  return partial && anySelected;
};

/**
 * Checks if the item is fully selected based on the isSelected function.
 * Returns true if all children are selected, false otherwise.
 */
export const isFullySelected = (item: NestedCheckboxItem, isSelected: (id: string) => boolean): boolean => {
  if (!item.children || item.children.length === 0) return isSelected(item.id);

  return item.children.some((child: NestedCheckboxItem) => {
    return isSelected(child.id) || isFullySelected(child, isSelected);
  });
};

/**
 * Finds the parent item of a children
 */
export const findParent = (childId: string, nodes: NestedCheckboxItem[]): NestedCheckboxItem | undefined => {
  for (const node of nodes) {
    if (node.children?.some((child) => child.id === childId)) {
      return node;
    }
    const foundInDescendants = findParent(childId, node.children || []);
    if (foundInDescendants) {
      return foundInDescendants;
    }
  }
  return undefined;
};

/**
 * Updates the parent states based on the local selection of children nodes.
 * Recurevely searches for parents to uptade its state (select it or convert it to indeterminated)
 */
export const updateParentStates = (
  node: NestedCheckboxItem,
  nodes: NestedCheckboxItem[],
  localSelection: Set<string>
) => {
  let currentNode: NestedCheckboxItem | undefined = node;
  while (currentNode) {
    const parent = findParent(currentNode.id, nodes);
    if (!parent) {
      break;
    }

    const allChildrenSelected = parent.children?.every((child) => localSelection.has(child.id)) ?? false;
    const anyChildrenSelected = parent.children?.some((child) => localSelection.has(child.id)) ?? false;

    if (allChildrenSelected) {
      localSelection.add(parent.id);
    } else if (!anyChildrenSelected) {
      localSelection.delete(parent.id);
    }

    currentNode = parent;
  }
};

/**
 * Toggles a set of ids in the selection to true o false depending on the shouldBeSelected Value
 * it uses recursion to update the children states as well
 */
export const toggleSelection = (node: NestedCheckboxItem, shouldBeSelected: boolean, localSelection: Set<string>) => {
  if (shouldBeSelected) localSelection.add(node.id);
  else localSelection.delete(node.id);

  node.children?.forEach((child) => toggleSelection(child, shouldBeSelected, localSelection));
};

/**
 * Finds a node with a specific id in a nested structure of nodes.
 */
export const getItem = (id: string, nodes: NestedCheckboxItem[]): NestedCheckboxItem | undefined => {
  for (const node of nodes) {
    if (node.id === id) return node;
    const foundChild = node.children && getItem(id, node.children);
    if (foundChild) return foundChild;
  }
  return undefined;
};

/**
 * Recursevely Returns all items on the whole data as a flat array
 */
const getAllItems = (nodes: NestedCheckboxItem[], ids: NestedCheckboxItem[]) => {
  for (const node of nodes) {
    ids.push(node);
    if (node.children && node.children.length > 0) {
      getAllItems(node.children, ids);
    }
  }
  return ids;
};

/**
 * Recursevely Returns only items that have children (are parents)
 */
const getParentItems = (nodes: NestedCheckboxItem[], ids: NestedCheckboxItem[]) => {
  for (const node of nodes) {
    if (node.children && node.children.length > 0) {
      ids.push(node);
      getParentItems(node.children, ids);
    }
  }
  return ids;
};

/**
 * Recursevelly Returns de number of ids from a data array
 */
export const countIDs = (data: NestedCheckboxItem[]) => getAllItems(data, []).length;

/**
 * Recursevely Returns only ids that have children (are parents)
 */
export const getParentsIds = (data: NestedCheckboxItem[]): string[] => getParentItems(data, []).map((node) => node.id);

/**
 * Returns all the children ids from a previous selection removing all those ids who are parents
 */
export const getChildrenIds = (ids: string[], data: NestedCheckboxItem[]): string[] =>
  ids.reduce((acc: string[], id: string) => {
    const node = getItem(id, data);
    if (node && (!node.children || node.children.length === 0)) {
      acc.push(id);
    }
    return acc;
  }, []);

/**
 * Returns only the parent ids from a previus selection removing all those ids who are children
 */

export const getParentsIdsFromSelection = (ids: string[], data: NestedCheckboxItem[]): string[] =>
  ids.reduce((acc: string[], id: string) => {
    const node = getItem(id, data);
    if (node?.children && node.children.length > 0) {
      acc.push(node.id);
    }
    return acc;
  }, []);

/**
 * Recursevely Returns all ids on the whole data
 */
export const getAllIds = (data: NestedCheckboxItem[]) => getAllItems(data, []).map((node) => node.id);

/**
 * Returns the number of selectable ids (meaning those that don't have children)
 */
export const getSelectableIdsCount = (data: NestedCheckboxItem[]) => {
  const allIds = getAllIds(data);
  return getChildrenIds(allIds, data).length;
};

/**
 * Normalizes a string by converting it to lowercase and removing diacritics.
 */
const normalizeString = (str: string) =>
  str
    .toLowerCase()
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '');

/**
 * Recursively filters a hierarchical data structure based on a name search,
 * including any parent nodes of found items.
 */
export const filterByName = (name: string, data: NestedCheckboxItem[]): NestedCheckboxItem[] => {
  const normalizedSearch = normalizeString(name);

  const filterRecursive = (nodes: NestedCheckboxItem[]): NestedCheckboxItem[] => {
    return nodes.reduce<NestedCheckboxItem[]>((acc, node) => {
      if (normalizeString(node.name).includes(normalizedSearch)) {
        acc.push(node);
      } else if (node.children && node.children.length > 0) {
        const filteredChildren = filterRecursive(node.children);
        if (filteredChildren.length > 0) {
          acc.push({ ...node, children: filteredChildren });
        }
      }
      return acc;
    }, []);
  };

  return filterRecursive(data);
};
